import React, { Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { 
  ChatBubbleLeftIcon, 
  Cog6ToothIcon, 
  UserIcon,
  CpuChipIcon,
  CloudArrowUpIcon,
  PhotoIcon,
  SparklesIcon,
  MagnifyingGlassIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  Bars3Icon,
  XMarkIcon,
  SunIcon,
  MoonIcon
} from '@heroicons/react/24/outline';

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  // Core navigation items (always visible based on permissions)
  const coreNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: UserIcon, show: true },
    { name: 'Chat', href: '/chat', icon: ChatBubbleLeftIcon, show: user?.can_chat },
    { name: 'Agents', href: '/agents', icon: CpuChipIcon, show: user?.can_use_agents },
  ];

  // Media dropdown items (grouped together)
  const mediaItems = [
    { name: 'Upload Media', href: '/upload', icon: CloudArrowUpIcon, description: 'Upload and process files' },
    { name: 'Gallery', href: '/gallery', icon: PhotoIcon, description: 'Browse your media library' },
    { name: 'Image Effects', href: '/effects', icon: SparklesIcon, description: 'Apply artistic effects' },
    { name: 'Memory Search', href: '/memory', icon: MagnifyingGlassIcon, description: 'Search across all content' },
  ];

  // Admin navigation
  const adminNavigation = user?.role === 'admin' 
    ? [{ name: 'Admin', href: '/admin', icon: Cog6ToothIcon, show: true }]
    : [];

  // Check if any media item is active
  const isMediaActive = mediaItems.some(item => location.pathname === item.href);

  // Check if user has media access
  const hasMediaAccess = user?.can_upload;

  const MediaDropdown = () => {
    if (!hasMediaAccess) return null;

    return (
      <Menu as="div" className="relative inline-block text-left">
        <div>
          <Menu.Button className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors duration-200 group ${
            isMediaActive
              ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
          }`}>
            <PhotoIcon className="w-4 h-4 mr-1" />
            Media
            <ChevronDownIcon className="w-3 h-3 ml-1 transition-transform duration-200 group-hover:rotate-180" />
          </Menu.Button>
        </div>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute left-0 z-50 mt-2 w-72 origin-top-left rounded-md bg-white dark:bg-dark-secondary py-1 shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-600 focus:outline-none">
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-600">
              <p className="text-sm font-medium text-gray-900 dark:text-dark-primary">Media Tools</p>
              <p className="text-xs text-gray-500 dark:text-dark-secondary">Upload, manage, and enhance your content</p>
            </div>
            {mediaItems.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Menu.Item key={item.name}>
                  {({ active }) => (
                    <Link
                      to={item.href}
                      className={`group flex items-start px-4 py-3 text-sm transition-colors duration-150 ${
                        active || isActive
                          ? 'bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400'
                          : 'text-gray-700 dark:text-dark-secondary hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <item.icon className={`w-5 h-5 mr-3 mt-0.5 flex-shrink-0 ${
                        active || isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-400'
                      }`} />
                      <div>
                        <div className="font-medium">{item.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.description}</div>
                      </div>
                      {isActive && (
                        <div className="ml-auto">
                          <div className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full"></div>
                        </div>
                      )}
                    </Link>
                  )}
                </Menu.Item>
              );
            })}
          </Menu.Items>
        </Transition>
      </Menu>
    );
  };

  const MobileMenu = () => (
    <Transition
      show={mobileMenuOpen}
      as={Fragment}
      enter="duration-200 ease-out"
      enterFrom="opacity-0 scale-95"
      enterTo="opacity-100 scale-100"
      leave="duration-100 ease-in"
      leaveFrom="opacity-100 scale-100"
      leaveTo="opacity-0 scale-95"
    >
      <div className="absolute top-16 inset-x-0 z-50 origin-top-right transform p-2 transition lg:hidden">
        <div className="divide-y-2 divide-gray-50 dark:divide-gray-700 rounded-lg bg-white dark:bg-dark-secondary shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-600">
          <div className="px-5 pt-5 pb-6">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-lg font-bold text-blue-600 dark:text-blue-400">🤖 Jarvis AI</span>
              </div>
              <div className="-mr-2">
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-md bg-white dark:bg-dark-secondary p-2 text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-500 dark:hover:text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="sr-only">Close menu</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
            </div>
            
            {/* Core Navigation - Mobile */}
            <div className="mt-6">
              <nav className="grid gap-y-1">
                {coreNavigation.filter(item => item.show).map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`flex items-center rounded-md px-3 py-3 text-base font-medium transition-colors duration-150 ${
                        isActive
                          ? 'bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400'
                          : 'text-gray-700 dark:text-dark-secondary hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-dark-primary'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <item.icon className="w-6 h-6 mr-3" />
                      {item.name}
                    </Link>
                  );
                })}
              </nav>
            </div>

            {/* Media Items - Mobile */}
            {hasMediaAccess && (
              <div className="mt-6">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-gray-600 dark:text-dark-secondary">Media Tools</p>
                </div>
                <nav className="grid gap-y-1">
                  {mediaItems.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex items-center rounded-md px-3 py-3 text-base font-medium transition-colors duration-150 ${
                          isActive
                            ? 'bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-dark-secondary hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-dark-primary'
                        }`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <item.icon className="w-6 h-6 mr-3" />
                        {item.name}
                      </Link>
                    );
                  })}
                </nav>
              </div>
            )}

            {/* Admin - Mobile */}
            {adminNavigation.length > 0 && (
              <div className="mt-6">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-gray-600 dark:text-dark-secondary">Administration</p>
                </div>
                <nav className="grid gap-y-1">
                  {adminNavigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex items-center rounded-md px-3 py-3 text-base font-medium transition-colors duration-150 ${
                          isActive
                            ? 'bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-dark-secondary hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-dark-primary'
                        }`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <item.icon className="w-6 h-6 mr-3" />
                        {item.name}
                      </Link>
                    );
                  })}
                </nav>
              </div>
            )}
          </div>
          
          {/* User section - Mobile */}
          <div className="px-5 py-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                  <UserIcon className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                </div>
              </div>
              <div className="ml-3">
                <div className="text-base font-medium text-gray-800 dark:text-dark-primary">
                  {user?.full_name || user?.username}
                </div>
                <div className="text-sm font-medium text-gray-500 dark:text-dark-secondary">
                  {user?.role} • {user?.email}
                </div>
              </div>
            </div>
            <div className="mt-4 space-y-1">
              <button
                onClick={() => {
                  toggleTheme();
                  setMobileMenuOpen(false);
                }}
                className="flex w-full items-center rounded-md px-3 py-3 text-base font-medium text-gray-700 dark:text-dark-secondary hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-dark-primary"
              >
                {theme === 'dark' ? (
                  <SunIcon className="w-6 h-6 mr-3" />
                ) : (
                  <MoonIcon className="w-6 h-6 mr-3" />
                )}
                {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </button>
              <button
                onClick={() => {
                  logout();
                  setMobileMenuOpen(false);
                }}
                className="flex w-full items-center rounded-md px-3 py-3 text-base font-medium text-gray-700 dark:text-dark-secondary hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-dark-primary"
              >
                <ArrowRightOnRectangleIcon className="w-6 h-6 mr-3" />
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );

  return (
    <>
      <nav className="bg-white dark:bg-dark-secondary shadow-lg border-b border-gray-200 dark:border-dark-primary relative transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Left side - Logo and Navigation */}
            <div className="flex items-center">
              <Link to="/dashboard" className="flex-shrink-0 flex items-center">
                <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">🤖 Jarvis AI</span>
                <span className="ml-2 text-sm bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 px-2 py-1 rounded">Phase 5</span>
              </Link>
              
              {/* Desktop Navigation */}
              <div className="hidden lg:ml-8 lg:flex lg:space-x-4">
                {/* Core Navigation Items */}
                {coreNavigation.filter(item => item.show).map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`inline-flex items-center px-3 py-2 border-b-2 text-sm font-medium transition-colors duration-200 ${
                        isActive
                          ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <item.icon className="w-4 h-4 mr-2" />
                      {item.name}
                    </Link>
                  );
                })}

                {/* Media Dropdown */}
                <MediaDropdown />

                {/* Admin Navigation */}
                {adminNavigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`inline-flex items-center px-3 py-2 border-b-2 text-sm font-medium transition-colors duration-200 ${
                        isActive
                          ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <item.icon className="w-4 h-4 mr-2" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Right side - User info and actions */}
            <div className="flex items-center">
              {/* Desktop User Section */}
              <div className="hidden md:flex md:items-center md:space-x-4">
                {/* Theme Toggle */}
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
                >
                  {theme === 'dark' ? (
                    <SunIcon className="w-5 h-5" />
                  ) : (
                    <MoonIcon className="w-5 h-5" />
                  )}
                </button>

                <div className="flex items-center space-x-3">
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900 dark:text-dark-primary">
                      {user?.full_name || user?.username}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-dark-secondary capitalize">
                      {user?.role}
                    </div>
                  </div>
                  <div className="h-8 w-8 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                    <UserIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                  </div>
                </div>
                <button
                  onClick={logout}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none transition"
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4 mr-1" />
                  <span className="hidden xl:inline">Logout</span>
                </button>
              </div>

              {/* Mobile menu button */}
              <div className="lg:hidden">
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-md bg-white dark:bg-dark-secondary p-2 text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-500 dark:hover:text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                  onClick={() => setMobileMenuOpen(true)}
                >
                  <span className="sr-only">Open main menu</span>
                  <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <MobileMenu />
    </>
  );
}